import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';
import FormPreview from '../components/form-builder/FormPreview';

// API base URL sabitini tanımla
const API_BASE_URL = 'https://app.hhoms.com/api';

const PublicFormView = () => {
  const navigate = useNavigate();
  const { token } = useParams(); // Public token
  const appToken = useSelector((state) => state.appToken?.apptoken);
  const userData = useSelector((state) => state.loginData?.loginData);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(null);
  const [formContent, setFormContent] = useState(null);

  useEffect(() => {
    const loadPublicForm = async () => {
      try {
        console.log('Public form yükleniyor, token:', token);
        
        // Token direkt formId olarak kullan
        const formId = token;
        
        if (!formId) {
          setError('Geçersiz public link');
          return;
        }

        if (!appToken || !userData) {
          setError('Uygulama yetkilendirmesi gerekli');
          setLoading(false);
          return;
        }
        
        console.log('FormId:', formId);
        
        // Mevcut form API'sini kullan - headers ile
        const response = await axios.get(
          `${API_BASE_URL}/form/Libraries/${formId}`,
          {
            headers: {
              ApplicationToken: appToken,
              User: userData.id,
            },
            timeout: 30000 // Timeout'u artır
          }
        );

        console.log('Form API yanıtı:', response.data);

        if (response.data && response.data.formLibraries && response.data.formLibraries[0]) {
          const formLibrary = response.data.formLibraries[0];
          
          // formContent'ı parse et
          let formContentObj = {};
          if (formLibrary.formContent) {
            try {
              if (typeof formLibrary.formContent === 'string') {
                formContentObj = JSON.parse(formLibrary.formContent);
              } else {
                formContentObj = formLibrary.formContent;
              }
              setFormContent(formContentObj);
              console.log('Parse edilmiş formContent:', formContentObj);
            } catch (parseError) {
              console.error('Form içeriği parse edilemedi:', parseError);
              setError('Form içeriği okunamadı');
              return;
            }
          }

          setFormData({
            id: formLibrary.id,
            title: formLibrary.title || formContentObj.title || 'Form',
            description: formContentObj.description || '',
            fields: formContentObj.fields || [],
            settings: formContentObj.settings || {},
            logo: formContentObj.logo,
            calculationValues: {}
          });
        } else {
          setError('Form bulunamadı veya erişim izni yok');
        }
        
      } catch (error) {
        console.error('Public form yüklenirken hata:', error);
        if (error.response?.status === 404) {
          setError('Form bulunamadı');
        } else if (error.response?.status === 403) {
          setError('Bu forma erişim izniniz yok');
        } else {
          setError('Form yüklenirken hata oluştu');
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadPublicForm();
    } else {
      setError('Geçersiz link');
      setLoading(false);
    }
  }, [token]);

  const handleBackToHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Form yükleniyor...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          onClick={handleBackToHome} 
          startIcon={<ArrowBackIcon />}
          variant="contained"
        >
          Ana Sayfaya Dön
        </Button>
      </Container>
    );
  }

  if (!formData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Form verileri bulunamadı
        </Alert>
        <Button 
          onClick={handleBackToHome} 
          startIcon={<ArrowBackIcon />}
          variant="contained"
        >
          Ana Sayfaya Dön
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <AppBar position="static" sx={{ mb: 3, borderRadius: 1 }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBackToHome}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <VisibilityIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {formData?.title} - Önizleme Modu
          </Typography>
          <LockIcon sx={{ ml: 2 }} />
          <Typography variant="body2" sx={{ ml: 1 }}>
            Sadece Görüntüleme
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Uyarı Mesajı */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body1">
          <strong>Bu form önizleme modunda açılmıştır.</strong>
          <br />
          Formu görüntüleyebilir ve alanları doldurabilirsiniz ancak gönderim yapamazsınız.
        </Typography>
      </Alert>

      {/* Normal Form Preview - sadece gönderim disabled */}
      {formData && (
        <FormPreview
          id="public-form-preview"
          formTitle={formData.title}
          formDescription={formData.description}
          formFields={formData.fields}
          settings={formData.settings}
          logoUrl={formData.logo}
          calculationValues={formData.calculationValues}
          showSubmitButton={false}
          showPdfButton={false}
        />
      )}
    </Container>
  );
};

export default PublicFormView;
